import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Ref } from "react";

import { type WorkerShift } from "../api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "../api/useGetWorkerStripeAccount";
import { type SearchResultItem } from "../hooks/useOmnisearchResults";
import { type CopiedItemType } from "../OmnisearchResultsList";
import { WorkerShiftItem } from "./WorkerShiftItem";
import { WorkerStripeActionItem } from "./WorkerStripeActionItem";

interface ExpandedWorkerContentProps {
  workerId: string;
  resultIndex: number;
  workerStripeAccount: WorkerStripeAccount | undefined;
  workerShifts: WorkerShift[];
  isLoadingWorkerShifts: boolean;
  isLoadingWorkerStripeAccount: boolean;
  workerHasShifts: boolean;
  workerHasStripeAccount: boolean;
  selectedIndex: number;
  copiedItem: CopiedItemType | null;
  onSelect: (item: SearchResultItem) => void;
  registerRef: (index: number) => (element: HTMLElement | null) => void;
}

export function ExpandedWorkerContent(props: ExpandedWorkerContentProps) {
  const {
    workerId,
    resultIndex,
    workerStripeAccount,
    workerShifts,
    isLoadingWorkerShifts,
    isLoadingWorkerStripeAccount,
    workerHasShifts,
    workerHasStripeAccount,
    selectedIndex,
    copiedItem,
    onSelect,
    registerRef,
  } = props;

  const stripeOffset = workerHasStripeAccount ? 1 : 0;

  return (
    <>
      {!isLoadingWorkerStripeAccount &&
        workerHasStripeAccount &&
        isDefined(workerStripeAccount) && (
          <WorkerStripeActionItem
            ref={registerRef(resultIndex + 1) as Ref<HTMLDivElement>}
            stripeAccount={workerStripeAccount}
            selected={selectedIndex === resultIndex + 1}
            copied={copiedItem === "stripe" && selectedIndex === resultIndex + 1}
            onClick={() => {
              onSelect({
                type: "agent-stripe",
                data: workerStripeAccount,
                workerId,
              });
            }}
          />
        )}
      {!isLoadingWorkerShifts && workerHasShifts && (
        <>
          {workerShifts.map((workerShift, shiftIndex) => {
            const shiftResultIndex = resultIndex + 1 + stripeOffset + shiftIndex;
            return (
              <WorkerShiftItem
                key={workerShift.id}
                ref={registerRef(shiftResultIndex) as Ref<HTMLDivElement>}
                shift={workerShift}
                selected={selectedIndex === shiftResultIndex}
                copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                onClick={() => {
                  onSelect({
                    type: "agent-shift",
                    data: workerShift,
                    workerId,
                  });
                }}
              />
            );
          })}
        </>
      )}
      {!isLoadingWorkerShifts &&
        !isLoadingWorkerStripeAccount &&
        !workerHasShifts &&
        !workerHasStripeAccount && (
          <Box sx={{ paddingLeft: 6, paddingRight: 2, paddingY: 1 }}>
            <Text variant="caption" color="text.secondary">
              No Stripe account or recent shifts
            </Text>
          </Box>
        )}
    </>
  );
}
